/* eslint-disable import/no-default-export */
// @flow

import React from 'react';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import Checkmark from 'dibs-vg/dist/react/checkmark';
import FailedIcon from 'dibs-vg/dist/react/upload-failed';
import XMark from 'dibs-vg/dist/react/x-mark';
import getter from 'dibs-object-getter';

import { graphql, createFragmentContainer } from 'react-relay';

import style from './style.scss';

import requestShippingFlow from './flows/requestShipping';
import requestHoldFlow from './flows/requestHold';
import transferOfferFlow from './flows/transferOffer';

import type {
    RequestFlowType,
    RequestFlowCopyReturnType,
    BulkOperationResultDisplayProps,
    ItemListProps,
} from './types';

function ItemList({
    headline,
    items,
    Icon,
    iconClassName,
    failureReasons = [],
    dataTn,
}: ItemListProps) {
    if (!items || (items && !items.length)) {
        return null;
    }

    return (
        <React.Fragment>
            <div className={style.headline} data-tn="request-headline">
                {headline}
            </div>

            <ul className={style.list} data-tn={dataTn}>
                {items.map(item => {
                    const failureReasonObj = failureReasons.find(r => r.itemId === item.serviceId);
                    const failureReasonText = getter(failureReasonObj).value('text');
                    return (
                        <li key={item.serviceId || item.title} className={style.listItem}>
                            <span className={style.iconWrapper}>
                                <Icon className={iconClassName} />
                            </span>
                            <div className={style.titleWrapper} data-tn="request-item-title">
                                {item.title}
                                {failureReasonText && (
                                    <div
                                        className={style.failureReasonWrapper}
                                        data-tn="request-failure-message"
                                    >
                                        {failureReasonText}
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </React.Fragment>
    );
}

function getFlow(flowType: RequestFlowType): RequestFlowCopyReturnType {
    switch (flowType) {
        case 'REQUEST_SHIPPING':
            return requestShippingFlow;

        case 'REQUEST_HOLD':
            return requestHoldFlow;

        case 'TRANSFER_OFFER':
            return transferOfferFlow;
    }
    return {};
}

function BulkOperationResultDisplay(props: BulkOperationResultDisplayProps) {
    const { flowType, bulkOperationResult, conversations } = props;
    const {
        barHeaderTitleSent,
        barHeaderTitleFailed,
        barHeaderSubtitle,
        successHeadline,
        failHeadline,
        failureReasons = {},
    } = getFlow(flowType);

    /**
     * itemIds should be the same as items associated to conversations except when it's a general inquiry and there is no item in conversation.
     * In latter case we create `item` and `failureCode` objects with pre-specified item title and failure code.
     */
    const genInquiryItems = [];
    const genInquiryFailureCodes = [];
    if (conversations && conversations.length) {
        conversations.forEach(conv => {
            if (!conv.item) {
                const sellerName = getter(conv)
                    .at('originalSeller')
                    .value('displayName');
                const titlePostfix = sellerName ? `to ${sellerName}` : '';
                genInquiryItems.push({
                    title: `General Inquiry ${titlePostfix}`,
                    serviceId: null,
                });
                genInquiryFailureCodes.push({
                    itemId: null,
                    failureCode: 'GENERAL_INQUIRY',
                });
            }
        });
    }

    const failureCodes = (getter(bulkOperationResult).value('failureCodes') || []).concat(
        genInquiryFailureCodes
    );
    const itemsFailed = (getter(bulkOperationResult).value('itemsFailed') || []).concat(
        genInquiryItems
    );
    const itemsSucceeded = getter(bulkOperationResult).value('itemsSucceeded') || [];

    const mapFailureCodeToReason = failureCodes.map(({ failureCode, itemId }) => ({
        itemId,
        text: failureReasons[failureCode] || failureReasons.SYSTEM_ERROR,
    }));

    const showFailedHeader =
        itemsFailed && itemsFailed.length && !(itemsSucceeded && itemsSucceeded.length);
    const title = showFailedHeader ? barHeaderTitleFailed : barHeaderTitleSent;
    const subtitle = showFailedHeader ? '' : barHeaderSubtitle;

    return (
        <div data-tn="shipping-request-modal-bulk-items-success">
            {showFailedHeader ? (
                <FailedIcon className={style.failedIcon} />
            ) : (
                <Checkmark className={style.successIcon} />
            )}
            <BarHeader title={title || ''} subtitle={subtitle} />

            <ModalBody>
                <ItemList
                    dataTn="request-item-list-success"
                    headline={successHeadline}
                    items={itemsSucceeded}
                    Icon={Checkmark}
                    iconClassName={style.checkMark}
                />

                <ItemList
                    dataTn="request-item-list-failed"
                    headline={failHeadline}
                    items={itemsFailed}
                    conversations={conversations}
                    Icon={XMark}
                    iconClassName={style.xMark}
                    failureReasons={mapFailureCodeToReason}
                />
            </ModalBody>
        </div>
    );
}

export { BulkOperationResultDisplay };

export default createFragmentContainer(BulkOperationResultDisplay, {
    bulkOperationResult: graphql`
        fragment BulkOperationResultDisplay_bulkOperationResult on BulkOperationResult {
            itemsSucceeded {
                title
                serviceId
                hasRequestedHoldForBuyer(buyerId: $userId)
                projects(userId: $userId) {
                    serviceId
                    name
                }
            }
            itemsFailed {
                title
                serviceId
                projects(userId: $userId) {
                    serviceId
                    name
                }
            }
            failureCodes {
                itemId
                failureCode
            }
        }
    `,
});
